import styled from 'styled-components';

import { ACQ, GRADIENTS } from '../../../../theme/tokens';
import { Text } from '../../../../components';

export const Root = styled.div`
  /* Same outer surface as the Self-managed Channels card. */
  background: ${ACQ.cardOuter};
  border-radius: 16px;
  padding: 10px;
  container-type: inline-size;
`;

/** Title row — heading on the left, the time-range selector pinned right. */
export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 6px 0;
  margin: 0 0 10px;
`;

export const H2 = styled(Text)`
  display: block;
  font-size: 22px;
  font-weight: 700;
  color: ${ACQ.white};
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @container (max-width: 860px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @container (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Kpi = styled.div`
  background: ${ACQ.campCard};
  border-radius: 16px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0 5px;
`;

export const Title = styled(Text)`
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: ${ACQ.t300};
  line-height: 1.2;
`;

export const Sub = styled(Text)`
  display: block;
  font-size: 14px;
  font-weight: 300;
  color: ${ACQ.t300};
  margin-top: 5px;
`;

export const Body = styled.div`
  display: flex;
  align-items: stretch;
  gap: 12px;
  background: ${ACQ.panel};
  border-radius: 16px;
  padding: 12px;
  margin-top: auto;
`;

export const ColMain = styled.div<{ $right?: boolean }>`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${(p) => p.$right && 'align-items: flex-end; text-align: right;'}
`;

export const VRule = styled.div`
  width: 1px;
  background: ${GRADIENTS.dividerStyledV};
`;

export const Label = styled(Text)`
  font-size: 12px;
  color: ${ACQ.white};
`;

export const Value = styled(Text)`
  font-size: 24px;
  font-weight: 700;
  color: ${ACQ.t50};
  margin-top: 2px;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
`;

/**
 * Pill on its own line, caption underneath — always, at every width.
 *
 * This used to be a wrapping row, which made the row's HEIGHT depend on the pill's text
 * width: a narrow `–` pill fits beside its caption where a wider `0%` or `+42%` pill does
 * not. Two tiles in the same grid row could therefore end up one line apart, and since the
 * panel is bottom-anchored in a stretched cell, the shorter one visibly sank. Stacking is
 * the only arrangement whose height does not depend on what the pill happens to say.
 */
export const Delta = styled.div<{ $right?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 4px;
  align-items: ${(p) => (p.$right ? 'flex-end' : 'flex-start')};
`;

export const DeltaVs = styled(Text)`
  font-size: 12px;
  color: ${ACQ.white};
`;

/** How a delta reads: it rose, it fell, or it did not move. `flat` covers both a figure
 *  that held steady and one with no comparison to make at all. */
export type DeltaTone = 'up' | 'down' | 'flat';

const TONE: Record<DeltaTone, { bg: string; fg: string }> = {
  up: { bg: ACQ.successBg, fg: ACQ.success },
  down: { bg: ACQ.dangerBg, fg: ACQ.danger },
  flat: { bg: ACQ.neutralBg, fg: ACQ.neutral },
};

export const Trend = styled.span<{ $tone: DeltaTone }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 500;
  padding: 4px 6px;
  border-radius: 6px;
  white-space: nowrap;
  background: ${(p) => TONE[p.$tone].bg};
  color: ${(p) => TONE[p.$tone].fg};
`;

export const TrendText = styled(Text)`
  font-size: 10px;
  font-weight: 500;
`;

